import { gql } from "@apollo/client";
import DropdownInput from "components/common/form/inputs/dropdown/DropdownInput";
import { graph } from "config/axios-context";
import { DropdownInputPropsType } from "types/form-types";
import { BookingChannelFilterInput, BookingChannelSortInput, CommissionTypes, GetChannelDropdownDataQuery, GetChannelDropdownDataQueryVariables } from "types/graphql";
import { appendFilters } from "utilities/datalist/datalist-filter-utilities";
import useAppContext from "utilities/hooks/useAppContext";

export type BookingChannelDropdownDataItemType = NonNullable<GetChannelDropdownDataQuery["bookingChannels"]>["items"][number];

type BookingChannelDropdownInputPropsType = Partial<DropdownInputPropsType<BookingChannelDropdownDataItemType, BookingChannelFilterInput, BookingChannelSortInput>> & {
}

const query = gql`
query GetChannelDropdownData($where: BookingChannelFilterInput, $order: [BookingChannelSortInput!], $skip: Int, $take: Int) {
    bookingChannels {
      items(order: $order, skip: $skip, take: $take, where: $where) {
        commission
        type
        name
        id,
        roomTypeDefultPrices {
          items {
            roomType {
              name
              id
              color
            }
            id
            defaultPrice
          }
        }
      }
    }
  }
`;

function getName(channel: BookingChannelDropdownDataItemType | undefined) : string {
    if(!channel) return '';

    return `${channel.name} (${channel.commission}${channel.type === CommissionTypes.Percent ? '%' : '€'})`;
}

const BookingChannelDropdownInput = (props: BookingChannelDropdownInputPropsType) => {
    const context = useAppContext();
    const config: DropdownInputPropsType<BookingChannelDropdownDataItemType, BookingChannelFilterInput, BookingChannelSortInput> = {
        getItemId(i) {
            return i.id;
        },
        view: {
            inputView: (i) => getName(i),
        },
        label: 'fields.channel.name',
        name: 'channel',
        dataSource: {
            request: async params => {
                let variable: GetChannelDropdownDataQueryVariables = {
                    order: params.sort,
                    skip: params.skip,
                    take: params.take,
                    where: params.filters,
                }

                if(params.searchText) {
                    const search: GetChannelDropdownDataQueryVariables["where"] = {
                        or: [
                            {
                                name: {
                                    contains: params.searchText,
                                }
                            }
                        ]
                    }
                    appendFilters(variable.where, search)
                }

                try {
                    const response = await graph<GetChannelDropdownDataQuery, GetChannelDropdownDataQueryVariables>({
                        query: query,
                        variables: variable,
                    });

                    if(response.errors?.length) {
                        throw response.errors;
                    }

                    return response.data!.bookingChannels!.items;
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0] : error);
                    throw error;
                }
            }
        }
    };

    const finalConfig = {...config, ...props};

    return (
        <DropdownInput
            {...finalConfig}
            value={props.value}
        />
    );
};

export default BookingChannelDropdownInput;
